import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  provideZonelessChangeDetection,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';

import { authInterceptor } from './core/api/interceptors/auth.interceptor';
import { caseMapperInterceptor } from './core/api/interceptors/case-mapper.interceptor';
import { errorNormaliserInterceptor } from './core/api/interceptors/error-normaliser.interceptor';
import { httpErrorInterceptor } from './core/api/interceptors/http-error.interceptor';
import { AppErrorHandler } from './core/errors/error-handler';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withViewTransitions(),
    ),
    provideHttpClient(
      withInterceptors([
        // Order matters (response flows innermost → outermost):
        //   1. auth — sees raw 401s, refreshes token, redirects to Auth0
        //   2. error-normaliser — converts remaining HTTP errors to AppError
        //   3. http-error — global UX (403 modal)
        //   4. case-mapper — camelCase ↔ snake_case on the wire
        caseMapperInterceptor,
        httpErrorInterceptor,
        errorNormaliserInterceptor,
        authInterceptor,
      ]),
    ),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
    ),
    { provide: ErrorHandler, useClass: AppErrorHandler },
  ],
};
